import { Group, MathUtils, Vector3, type PerspectiveCamera, type Scene } from 'three';
import { CharacterAnimator } from '../character/CharacterAnimator';
import type { CharacterConfig } from '../character/CharacterDefinition';
import type { CharacterInstance, CharacterLoader } from '../character/CharacterModel';
import { FishingRod } from '../character/FishingRod';
import { Weapon } from '../combat/Weapon';
import { INTERPOLATION_DELAY_MS, codeToAnim, type NetAppearance, type NetPlayer } from './protocol';

interface StateSample {
  time: number;
  x: number;
  y: number;
  z: number;
  r: number;
  a: number;
}

interface RemotePlayer {
  id: string;
  name: string;
  /** Kontejner, který existuje hned — model se do něj doplní, až se načte. */
  root: Group;
  character: CharacterInstance | null;
  animator: CharacterAnimator | null;
  /** Prut se vytváří až při prvním rybaření — většina hráčů nerybaří. */
  rod: FishingRod | null;
  /** Meč naopak nosí každý, takže se připíná hned s modelem. */
  weapon: Weapon | null;
  buffer: StateSample[];
  lastAnim: number;
  /** Byl minulý snímek pod hladinou? Přechod spouští šplouchnutí. */
  wasUnderwater: boolean;
  /** Ušlá dráha ve vodě od poslední vlny. */
  wadeDistance: number;
  /** Do jaké dráhy se pustí další vlna. Pokaždé jiná. */
  wadeStep: number;
  /** Poslední vykreslená pozice — z ní se počítá ušlá dráha ve vodě. */
  lastX: number;
  lastZ: number;
  tag: HTMLElement;
  disposed: boolean;
}

/**
 * Ostatní hráči ve světě.
 *
 * Dvě věci, na kterých stojí, jestli to bude vypadat jako hra nebo jako
 * trhaná tabulka čísel:
 *
 * 1. **Interpolace se zpožděním.** Vzdálení hráči se nevykreslují tam, kde
 *    jsou teď, ale tam, kde byli před {@link INTERPOLATION_DELAY_MS}. Díky
 *    tomu je vždy k dispozici snímek před i za aktuálním časem a pohyb je
 *    plynulý i při 12 Hz aktualizacích. Cena je konstantní zpoždění, které
 *    v kooperativním světě nikomu nevadí.
 * 2. **Model se načítá na pozadí.** Postava má několik megabajtů geometrie;
 *    dokud se nenačte, hráč už existuje a sbírá stavy do bufferu, takže po
 *    dokreslení naskočí rovnou na správné místo, ne do počátku scény.
 */
export class RemotePlayers {
  private readonly players = new Map<string, RemotePlayer>();
  private readonly tagLayer: HTMLElement;
  private readonly projected = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly loader: CharacterLoader,
    parent: HTMLElement,
  ) {
    this.tagLayer = document.createElement('div');
    this.tagLayer.id = 'net-tags';
    parent.appendChild(this.tagLayer);
  }

  get count(): number {
    return this.players.size;
  }

  add(net: NetPlayer): void {
    if (this.players.has(net.id)) return;

    const root = new Group();
    root.name = `remote_${net.id}`;
    root.position.set(net.p[0], net.p[1], net.p[2]);
    root.rotation.y = net.r;
    this.scene.add(root);

    const tag = document.createElement('div');
    tag.className = 'net-tag';
    tag.textContent = net.name;
    this.tagLayer.appendChild(tag);

    const player: RemotePlayer = {
      id: net.id,
      name: net.name,
      root,
      character: null,
      animator: null,
      rod: null,
      weapon: null,
      buffer: [{ time: performance.now(), x: net.p[0], y: net.p[1], z: net.p[2], r: net.r, a: net.a }],
      lastAnim: -1,
      wasUnderwater: false,
      wadeDistance: 0,
      wadeStep: 0.7,
      lastX: net.p[0],
      lastZ: net.p[2],
      tag,
      disposed: false,
    };
    this.players.set(net.id, player);

    void this.attachModel(player, net.appearance);
  }

  remove(id: string): void {
    const player = this.players.get(id);
    if (!player) return;
    player.disposed = true;
    player.rod?.dispose();
    player.weapon?.dispose();
    player.animator?.dispose();
    player.character?.dispose();
    player.root.removeFromParent();
    player.tag.remove();
    this.players.delete(id);
  }

  clear(): void {
    for (const id of [...this.players.keys()]) this.remove(id);
  }

  /** Nový stav ze sítě. `receivedAt` je čas z performance.now(). */
  pushState(
    id: string,
    p: [number, number, number],
    r: number,
    a: number,
    receivedAt: number,
  ): void {
    const player = this.players.get(id);
    if (!player) return;

    const buffer = player.buffer;
    const last = buffer[buffer.length - 1];
    // Zprávy mohou dorazit přeházené; starší než poslední zahodíme,
    // protože bychom jimi jen rozhoupali interpolaci.
    if (last && receivedAt < last.time) return;

    // Natočení se doplní o násobky 2π tak, aby se postava otáčela kratší
    // cestou. Bez toho udělá při přechodu přes ±π celou obrátku.
    const unwrapped = last ? last.r + shortestAngle(last.r, r) : r;

    buffer.push({ time: receivedAt, x: p[0], y: p[1], z: p[2], r: unwrapped, a });
    // Buffer se drží krátký — víc než sekunda historie k ničemu není.
    while (buffer.length > 2 && receivedAt - buffer[0].time > 1000) buffer.shift();
  }

  /** Světová pozice hráče, nebo null když ho neznáme. */
  positionOf(id: string): Vector3 | null {
    return this.players.get(id)?.root.position ?? null;
  }

  setAppearance(id: string, appearance: NetAppearance): void {
    const player = this.players.get(id);
    if (!player) return;
    void this.attachModel(player, appearance);
  }

  /**
   * Šplouchání ostatních hráčů.
   *
   * Po síti se nic dalšího neposílá — vodní efekt se odvodí z pozice, která
   * už chodí. Ušetří to zprávy a hlavně to nemůže rozejít: každý klient
   * vidí šplouchnutí přesně tam, kde vidí hráče.
   */
  onSplash: ((x: number, z: number, strength: number) => void) | null = null;
  onWade:
    | ((x: number, z: number, dirX: number, dirZ: number, strength: number) => void)
    | null = null;
  /** Výška hladiny a hloubka dna — musí se dodat zvenčí, síť je nezná. */
  waterProbe: ((x: number, z: number) => { seaLevel: number; groundY: number }) | null = null;

  /** Volat jednou za snímek po aktualizaci kamery. */
  update(dt: number, camera: PerspectiveCamera, canvas: HTMLElement): void {
    const renderTime = performance.now() - INTERPOLATION_DELAY_MS;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    for (const player of this.players.values()) {
      const previousY = player.root.position.y;
      this.applyInterpolated(player, renderTime);
      this.updateSplashes(player, previousY, dt);
      player.animator?.update(dt);
      this.positionTag(player, camera, width, height);
    }
  }

  dispose(): void {
    this.clear();
    this.tagLayer.remove();
  }

  // --- vnitřní ----------------------------------------------------------

  private applyInterpolated(player: RemotePlayer, renderTime: number): void {
    const buffer = player.buffer;
    if (buffer.length === 0) return;

    // Najdi dvojici snímků, mezi které renderTime spadá.
    let older = buffer[0];
    let newer = buffer[buffer.length - 1];
    for (let i = 0; i < buffer.length - 1; i++) {
      if (buffer[i].time <= renderTime && buffer[i + 1].time >= renderTime) {
        older = buffer[i];
        newer = buffer[i + 1];
        break;
      }
    }

    let x: number;
    let y: number;
    let z: number;
    let r: number;

    if (renderTime <= buffer[0].time) {
      // Data ještě nedorazila (hráč se právě objevil) — stůj na prvním snímku.
      ({ x, y, z, r } = buffer[0]);
    } else if (renderTime >= newer.time && older === newer) {
      ({ x, y, z, r } = newer);
    } else {
      const span = newer.time - older.time;
      const t = span > 0 ? MathUtils.clamp((renderTime - older.time) / span, 0, 1) : 1;
      x = older.x + (newer.x - older.x) * t;
      y = older.y + (newer.y - older.y) * t;
      z = older.z + (newer.z - older.z) * t;
      r = older.r + (newer.r - older.r) * t;
    }

    player.root.position.set(x, y, z);
    player.root.rotation.y = r;

    const animCode = newer.a;
    if (animCode !== player.lastAnim) {
      player.lastAnim = animCode;
      const state = codeToAnim(animCode);
      player.animator?.applyRemoteState(state);
      const fishing = state.startsWith('fish_');
      this.syncRod(player, fishing);
      // Prut a meč se v jedné ruce nepotkají.
      player.weapon?.show(!fishing);
      if (player.animator) player.animator.holdingItem = !fishing && !!player.weapon;
    }
  }

  private updateSplashes(player: RemotePlayer, previousY: number, dt: number): void {
    if (!this.waterProbe || (!this.onSplash && !this.onWade)) return;

    const position = player.root.position;
    const { seaLevel, groundY } = this.waterProbe(position.x, position.z);
    const inWater = seaLevel - groundY > 0.15 && position.y < seaLevel;

    if (inWater && !player.wasUnderwater) {
      // Rychlost dopadu z rozdílu dvou vykreslených pozic. Interpolace ji
      // sice trochu zjemní, ale na rozlišení "spadl" vs "vešel" to stačí.
      const fallSpeed = dt > 0 ? Math.abs(previousY - position.y) / dt : 0;
      this.onSplash?.(position.x, position.z, Math.max(0.18, Math.min(1, fallSpeed / 12)));
      player.wadeDistance = 0;
    }
    player.wasUnderwater = inWater;

    if (!inWater) return;
    const dx = position.x - player.lastX;
    const dz = position.z - player.lastZ;
    const moved = Math.hypot(dx, dz);
    player.lastX = position.x;
    player.lastZ = position.z;
    player.wadeDistance += moved;
    if (player.wadeDistance < player.wadeStep) return;
    player.wadeDistance = 0;
    // Nepravidelný rozestup, jinak vznikne za hráčem tištěná řada kruhů.
    player.wadeStep = 0.45 + Math.random() * 0.7;
    const dirX = moved > 1e-6 ? dx / moved : 0;
    const dirZ = moved > 1e-6 ? dz / moved : 0;
    this.onWade?.(position.x, position.z, dirX, dirZ, 0.3 + Math.random() * 0.25);
  }

  private positionTag(
    player: RemotePlayer,
    camera: PerspectiveCamera,
    width: number,
    height: number,
  ): void {
    // Jmenovka visí nad hlavou; 1.95 m odpovídá výšce postavy s rezervou.
    this.projected.set(player.root.position.x, player.root.position.y + 1.95, player.root.position.z);
    this.projected.project(camera);

    const behind = this.projected.z > 1;
    const offscreen =
      this.projected.x < -1.1 || this.projected.x > 1.1 || this.projected.y < -1.1 || this.projected.y > 1.1;

    if (behind || offscreen) {
      player.tag.style.display = 'none';
      return;
    }

    const distance = camera.position.distanceTo(player.root.position);
    player.tag.style.display = '';
    player.tag.style.transform = `translate(-50%, -100%) translate(${
      (this.projected.x * 0.5 + 0.5) * width
    }px, ${(-this.projected.y * 0.5 + 0.5) * height}px)`;
    // Vzdálené jmenovky blednou, aby z davu nebyla stěna textu.
    player.tag.style.opacity = String(MathUtils.clamp(1 - (distance - 25) / 35, 0.15, 1));
  }

  /**
   * Prut u vzdáleného hráče.
   *
   * Stav animace se po síti posílá, model prutu ne — musí se proto odvodit
   * z animace. Bez toho by kamarád rybařil prázdnýma rukama, což je jediná
   * část rybaření, které si na druhém hráči opravdu všimneš.
   */
  private syncRod(player: RemotePlayer, fishing: boolean): void {
    if (!player.character) return;

    if (!fishing) {
      player.rod?.show(false);
      return;
    }

    if (!player.rod) {
      const rod = new FishingRod(this.loader);
      if (!rod.attach(player.character)) return;
      void rod.setLevel(1);
      player.rod = rod;
    }
    player.rod.show(true);
  }

  private async attachModel(player: RemotePlayer, appearance: NetAppearance): Promise<void> {
    const config = appearanceToConfig(appearance);

    let instance: CharacterInstance;
    try {
      instance = await this.loader.createCharacter(config);
    } catch (error) {
      console.warn(`[net] model hráče ${player.name} se nepodařilo načíst:`, error);
      return;
    }

    // Hráč mohl mezitím odejít nebo si znovu změnit vzhled.
    if (player.disposed) {
      instance.dispose();
      return;
    }

    player.animator?.dispose();
    player.rod?.dispose();
    player.rod = null;
    player.weapon?.dispose();
    player.weapon = null;
    player.character?.root.removeFromParent();
    player.character?.dispose();

    player.character = instance;
    player.animator = new CharacterAnimator(instance);
    player.root.add(instance.root);

    // Meč dostane každý hráč rovnou — jinak ostatní bojují prázdnýma rukama.
    const weapon = new Weapon(this.loader);
    if (weapon.attach(instance)) {
      void weapon.setModel('Sword_Golden');
      weapon.show(true);
      player.weapon = weapon;
      player.animator.holdingItem = true;
    }

    // Vynulovat, aby se stav animace i prut po přezbrojení nastavily znovu.
    player.lastAnim = -1;
  }
}

function appearanceToConfig(appearance: NetAppearance): CharacterConfig {
  return {
    name: '',
    gender: appearance.gender as CharacterConfig['gender'],
    hairstyle: appearance.hairstyle as CharacterConfig['hairstyle'],
    beard: appearance.beard as CharacterConfig['beard'],
    eyebrows: appearance.eyebrows as CharacterConfig['eyebrows'],
    skinTone: appearance.skinTone as CharacterConfig['skinTone'],
    hairColor: appearance.hairColor as CharacterConfig['hairColor'],
    outfit: appearance.outfit as CharacterConfig['outfit'],
    hood: appearance.hood,
  };
}

/** Nejkratší rozdíl dvou úhlů, v rozsahu (-π, π]. */
function shortestAngle(from: number, to: number): number {
  let diff = (to - from) % (Math.PI * 2);
  if (diff > Math.PI) diff -= Math.PI * 2;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}
